import { useEffect, useState, useMemo } from 'react';
import { Badge, Button, Card, View } from '@aws-amplify/ui-react';
import { StorageImage } from '@aws-amplify/ui-react-storage';
import { ListPaginateWithPathOutput } from 'aws-amplify/storage';
import { useTranslation } from 'react-i18next';
import EmptyImageUtils from '../../utils/image/EmptyImageUtils';
import StorageUtils from '../../utils/StorageUtils';
import './ImageGallery.css';

interface ImageGalleryProps {
    images: ListPaginateWithPathOutput | null;
}

export default function ImageGallery({ images }: ImageGalleryProps) {
    const { t } = useTranslation();
    const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    // Sort images to ensure main image is first, and get main image index
    const { sortedImages, mainImageIndex } = useMemo(() => {
        if (!images?.items || images.items.length === 0) {
            return { sortedImages: null, mainImageIndex: -1 };
        }

        const items = [...images.items];
        const mainImageIdx = items.findIndex(item => 
            item.path.includes(StorageUtils.PLACE_PHOTOS_MAIN_IMAGE_PREFIX)
        );

        if (mainImageIdx > 0) {
            // Move main image to first position
            const mainImage = items[mainImageIdx];
            items.splice(mainImageIdx, 1);
            items.unshift(mainImage);
            return { sortedImages: { ...images, items }, mainImageIndex: 0 };
        }

        return { sortedImages: images, mainImageIndex: mainImageIdx };
    }, [images]);

    // Auto-select main image when component loads
    useEffect(() => {
        if (mainImageIndex >= 0) {
            setSelectedImageIndex(0);
        }
    }, [mainImageIndex]);

    // Check if current selected image is main image
    const isMainImage = selectedImageIndex === 0 && mainImageIndex === 0;

    const handleThumbnailClick = (index: number) => {
        setSelectedImageIndex(index);
    };

    const handleMainImageClick = () => {
        if (!images?.items || images.items.length === 0) return;
        setIsFullscreen(true);
    };

    const handleFullscreenClose = () => {
        setIsFullscreen(false);
    };

    const handlePrevImage = () => {
        if (displayImages?.items) {
            setSelectedImageIndex((prev) =>
                prev === 0 ? displayImages.items.length - 1 : prev - 1
            );
        }
    };

    const handleNextImage = () => {
        if (displayImages?.items) {
            setSelectedImageIndex((prev) =>
                prev === displayImages.items.length - 1 ? 0 : prev + 1
            );
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (!isFullscreen) return;

        switch (event.key) {
            case 'Escape':
                handleFullscreenClose();
                break;
            case 'ArrowLeft':
                handlePrevImage();
                break;
            case 'ArrowRight':
                handleNextImage();
                break;
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    const hasImages = sortedImages?.items && sortedImages.items.length > 0;
    const displayImages = sortedImages || images;

    return (
        <>
            <View className="new-image-gallery">
                <Card
                    className="new-main-image-container-card"
                    variation="elevated"
                    onClick={handleMainImageClick}
                >
                    {hasImages && displayImages ? (
                        <>
                            <StorageImage
                                className="new-main-image"
                                key={displayImages.items[selectedImageIndex].path}
                                alt=""
                                path={displayImages.items[selectedImageIndex].path}
                            />
                            {isMainImage && (
                                <Badge 
                                    className="main-image-badge"
                                    variation="info"
                                >
                                    {t('place.main-image')}
                                </Badge>
                            )}
                        </>
                    ) : (
                        <img
                            className="new-main-image"
                            src={EmptyImageUtils.getPlaceholderImage()}
                            alt={EmptyImageUtils.getPlaceholderImageAlt()}
                        />
                    )}
                    {hasImages && (
                        <>
                            <Button
                                className="new-gallery-nav-button prev"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrevImage();
                                }}
                            >
                                &lt;
                            </Button>
                            <Button
                                className="new-gallery-nav-button next"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNextImage();
                                }}
                            >
                                &gt;
                            </Button>
                        </>
                    )}
                </Card>
                <View className="new-thumbnails-container">
                    {hasImages && displayImages ? (
                        displayImages.items.map((image, index) => (
                            <StorageImage
                                key={image.path}
                                alt=""
                                path={image.path}
                                className={`new-thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                                onClick={() => handleThumbnailClick(index)}
                            />
                        ))
                    ) : (
                        <img
                            className="new-thumbnail active"
                            src={EmptyImageUtils.getPlaceholderImage()}
                            alt={EmptyImageUtils.getPlaceholderImageAlt()}
                        />
                    )}
                </View>
            </View>

            {isFullscreen && hasImages && displayImages && (
                <View className="new-fullscreen-overlay">
                    <View className="new-fullscreen-image-container">
                        {displayImages.items[selectedImageIndex] && (
                            <StorageImage
                                className="new-fullscreen-image"
                                key={displayImages.items[selectedImageIndex].path}
                                alt=""
                                path={displayImages.items[selectedImageIndex].path}
                            />
                        )}
                        {isMainImage && (
                            <Badge 
                                className="main-image-badge-fullscreen"
                                variation="info"
                            >
                                {t('place.main-image')}
                            </Badge>
                        )}
                        <Button
                            className="new-fullscreen-close-button"
                            onClick={handleFullscreenClose}
                        >
                            &times;
                        </Button>
                        <Button
                            className="new-fullscreen-nav-button prev"
                            onClick={handlePrevImage}
                        >
                            &lt;
                        </Button>
                        <Button
                            className="new-fullscreen-nav-button next"
                            onClick={handleNextImage}
                        >
                            &gt;
                        </Button>
                    </View>
                </View>
            )}
        </>
    );
} 